// =============================================
// COMPONENTE: FLOW EDITOR - MOBILE FIRST
// =============================================
// Arquivo: FlowEditor.tsx
// Descrição: Editor visual de fluxos com suporte touch completo
// =============================================

import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Connection,
  Edge,
  Node,
  MarkerType,
  Panel,
  NodeTypes
} from 'reactflow';
import 'reactflow/dist/style.css';

import StartNode from './nodes/StartNode';
import MessageNode from './nodes/MessageNode';
import InputNode from './nodes/InputNode';
import ConditionNode from './nodes/ConditionNode';
import ActionNode from './nodes/ActionNode';
import EndNode from './nodes/EndNode';
import WebhookNode from './nodes/WebhookNode';
import DelayNode from './nodes/DelayNode';
import DatabaseNode from './nodes/DatabaseNode';
import HTTPRequestNode from './nodes/HTTPRequestNode';
import JavaScriptFunctionNode from './nodes/JavaScriptFunctionNode';
import WebhookTriggerNode from './nodes/WebhookTriggerNode';
import InternalAPINode from './nodes/InternalAPINode';
import WhatsAppAdvancedNode from './nodes/WhatsAppAdvancedNode';
import LoopNode from './nodes/LoopNode';
import ErrorHandlerNode from './nodes/ErrorHandlerNode';
import EmailNode from './nodes/EmailNode';
import SchedulerNode from './nodes/SchedulerNode';
import ChecklistNode from './nodes/ChecklistNode';
import VehicleNode from './nodes/VehicleNode';
import DriverNode from './nodes/DriverNode';
import AbastecimentoNode from './nodes/AbastecimentoNode';
import MaintenanceNode from './nodes/MaintenanceNode';
import NotificationNode from './nodes/NotificationNode';
import ReportNode from './nodes/ReportNode';
import ScheduleNode from './nodes/ScheduleNode';
import MenuNode from './nodes/MenuNode';
import SwitchNode from './nodes/SwitchNode';
import SetVariablesNode from './nodes/SetVariablesNode';

import MobileToolbar from './MobileToolbar';
import NodeEditorModal from './NodeEditorModal';
import NodePalette from './NodePalette';
import ImportExportModal from './ImportExportModal';
import TemplatesModal from './TemplatesModal';
import FlowStatsPanel from './FlowStatsPanel';
import './FlowEditor.css';

// =============================================
// TIPOS DE NÓS PERSONALIZADOS
// =============================================

const nodeTypes: NodeTypes = {
  start: StartNode,
  message: MessageNode,
  input: InputNode,
  condition: ConditionNode,
  action: ActionNode,
  webhook: WebhookNode,
  delay: DelayNode,
  database: DatabaseNode,
  end: EndNode,
  http_request: HTTPRequestNode,
  javascript_function: JavaScriptFunctionNode,
  webhook_trigger: WebhookTriggerNode,
  internal_api: InternalAPINode,
  whatsapp_advanced: WhatsAppAdvancedNode,
  loop: LoopNode,
  error_handler: ErrorHandlerNode,
  email: EmailNode,
  scheduler: SchedulerNode,
  checklist: ChecklistNode,
  vehicle: VehicleNode,
  driver: DriverNode,
  abastecimento: AbastecimentoNode,
  fueling: AbastecimentoNode,
  maintenance: MaintenanceNode,
  manutencao: MaintenanceNode,
  notification: NotificationNode,
  report: ReportNode,
  schedule: ScheduleNode,
  menu: MenuNode,
  switch: SwitchNode,
  set_variables: SetVariablesNode
};

// =============================================
// CONFIGURAÇÕES DE EDGE PADRÃO
// =============================================

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: { stroke: '#667eea', strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#667eea'
  }
};

// =============================================
// INTERFACES
// =============================================

interface FlowEditorProps {
  initialNodes?: Node[];
  initialEdges?: Edge[];
  onSave?: (nodes: Node[], edges: Edge[]) => void;
  readOnly?: boolean;
}

// =============================================
// COMPONENTE INTERNO (COM ACESSO AO REACTFLOW)
// =============================================

const FlowEditorInner: React.FC<FlowEditorProps> = ({
  initialNodes = [],
  initialEdges = [],
  onSave,
  readOnly = false
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]); // Seleção múltipla
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMobileToolbarOpen, setIsMobileToolbarOpen] = useState(false);
  const [isNodePaletteOpen, setIsNodePaletteOpen] = useState(true);
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'diagram' | 'list'>('diagram');
  const [clipboard, setClipboard] = useState<{ nodes: Node[], edges: Edge[] } | null>(null);

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useReactFlow();

  // =============================================
  // DETECÇÃO DE MOBILE
  // =============================================

  const isMobile = window.innerWidth <= 768;

  // =============================================
  // HAPTIC FEEDBACK (VIBRAÇÃO TÁTIL)
  // =============================================

  const triggerHapticFeedback = useCallback((type: 'light' | 'medium' | 'heavy' = 'light') => {
    if (!isMobile || !navigator.vibrate) return;

    switch (type) {
      case 'light':
        navigator.vibrate(10);
        break;
      case 'medium':
        navigator.vibrate(25);
        break;
      case 'heavy':
        navigator.vibrate([50, 20, 50]);
        break;
    }
  }, [isMobile]);

  // =============================================
  // ZOOM INTELIGENTE - FIT VIEW AUTOMÁTICO
  // =============================================

  useEffect(() => {
    if (nodes.length > 0) {
      // Aguardar renderização dos nós e então ajustar zoom
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.2,
          duration: 500,
          maxZoom: 1
        });
      }, 100);
    }
  }, [nodes.length, reactFlowInstance]);

  // =============================================
  // HANDLER PARA CENTRALIZAR FLUXO
  // =============================================

  const handleFitView = useCallback(() => {
    reactFlowInstance.fitView({
      padding: 0.2,
      duration: 500,
      maxZoom: 1.5
    });
  }, [reactFlowInstance]);

  // =============================================
  // HANDLERS DE NÓS E EDGES
  // =============================================

  const onConnect = useCallback(
    (params: Connection) => {
      if (readOnly) return;

      triggerHapticFeedback('medium');
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            ...defaultEdgeOptions
          },
          eds
        )
      );
    },
    [setEdges, readOnly, triggerHapticFeedback]
  );

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (readOnly) return;
      triggerHapticFeedback('light');

      // Shift+Click para seleção múltipla
      if (event.shiftKey) {
        const isAlreadySelected = selectedNodes.some(n => n.id === node.id);
        if (isAlreadySelected) {
          // Remove da seleção
          setSelectedNodes(prev => prev.filter(n => n.id !== node.id));
          // Marca visualmente o nó como não selecionado
          setNodes(nds => nds.map(n =>
            n.id === node.id ? { ...n, selected: false } : n
          ));
        } else {
          // Adiciona à seleção
          setSelectedNodes(prev => [...prev, node]);
          // Marca visualmente o nó como selecionado
          setNodes(nds => nds.map(n =>
            n.id === node.id ? { ...n, selected: true } : n
          ));
        }
      } else {
        // Clique normal - seleciona apenas este nó
        setSelectedNode(node);
        setSelectedNodes([node]);
        // Desmarca todos e marca apenas este
        setNodes(nds => nds.map(n => ({
          ...n,
          selected: n.id === node.id
        })));
      }
    },
    [readOnly, triggerHapticFeedback, selectedNodes, setNodes]
  );

  const onNodeDoubleClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (readOnly) return;
      setSelectedNode(node);
      setIsEditModalOpen(true);
    },
    [readOnly]
  );

  // Handler para box selection (arrastar para selecionar múltiplos nós)
  const onSelectionChange = useCallback(
    (params: { nodes: Node[]; edges: Edge[] }) => {
      if (readOnly) return;
      setSelectedNodes(params.nodes);
      if (params.nodes.length === 1) {
        setSelectedNode(params.nodes[0]);
      } else if (params.nodes.length === 0) {
        setSelectedNode(null);
      }
    },
    [readOnly]
  );

  // =============================================
  // OPERAÇÕES EM LOTE
  // =============================================

  const alignNodes = useCallback((direction: 'left' | 'right' | 'top' | 'bottom' | 'center-h' | 'center-v') => {
    if (selectedNodes.length < 2) return;

    const positions = selectedNodes.map(n => n.position);
    let newPositions: { x: number; y: number }[];

    switch (direction) {
      case 'left':
        const minX = Math.min(...positions.map(p => p.x));
        newPositions = positions.map(p => ({ ...p, x: minX }));
        break;
      case 'right':
        const maxX = Math.max(...positions.map(p => p.x));
        newPositions = positions.map(p => ({ ...p, x: maxX }));
        break;
      case 'top':
        const minY = Math.min(...positions.map(p => p.y));
        newPositions = positions.map(p => ({ ...p, y: minY }));
        break;
      case 'bottom':
        const maxY = Math.max(...positions.map(p => p.y));
        newPositions = positions.map(p => ({ ...p, y: maxY }));
        break;
      case 'center-h':
        const avgY = positions.reduce((sum, p) => sum + p.y, 0) / positions.length;
        newPositions = positions.map(p => ({ ...p, y: avgY }));
        break;
      case 'center-v':
        const avgX = positions.reduce((sum, p) => sum + p.x, 0) / positions.length;
        newPositions = positions.map(p => ({ ...p, x: avgX }));
        break;
      default:
        return;
    }

    setNodes(nds => nds.map((node, idx) => {
      const selectedIdx = selectedNodes.findIndex(n => n.id === node.id);
      if (selectedIdx !== -1) {
        return { ...node, position: newPositions[selectedIdx] };
      }
      return node;
    }));
  }, [selectedNodes, setNodes]);

  const distributeNodes = useCallback((direction: 'horizontal' | 'vertical') => {
    if (selectedNodes.length < 3) return;

    const sorted = [...selectedNodes].sort((a, b) =>
      direction === 'horizontal'
        ? a.position.x - b.position.x
        : a.position.y - b.position.y
    );

    const first = sorted[0].position;
    const last = sorted[sorted.length - 1].position;
    const totalGap = direction === 'horizontal'
      ? last.x - first.x
      : last.y - first.y;
    const gap = totalGap / (sorted.length - 1);

    setNodes(nds => nds.map(node => {
      const sortedIdx = sorted.findIndex(n => n.id === node.id);
      if (sortedIdx !== -1) {
        const newPos = direction === 'horizontal'
          ? { ...node.position, x: first.x + (gap * sortedIdx) }
          : { ...node.position, y: first.y + (gap * sortedIdx) };
        return { ...node, position: newPos };
      }
      return node;
    }));
  }, [selectedNodes, setNodes]);

  const zoomToSelection = useCallback(() => {
    if (selectedNodes.length === 0) return;

    // Calcula bounding box dos nós selecionados
    const minX = Math.min(...selectedNodes.map(n => n.position.x));
    const minY = Math.min(...selectedNodes.map(n => n.position.y));
    const maxX = Math.max(...selectedNodes.map(n => n.position.x + 200)); // Largura estimada do nó
    const maxY = Math.max(...selectedNodes.map(n => n.position.y + 100)); // Altura estimada do nó

    // Adiciona padding
    const padding = 100;
    const bounds = {
      x: minX - padding,
      y: minY - padding,
      width: (maxX - minX) + (padding * 2),
      height: (maxY - minY) + (padding * 2)
    };

    reactFlowInstance.fitBounds(bounds, { padding: 0.2, duration: 400 });
  }, [selectedNodes, reactFlowInstance]);

  // =============================================
  // LONG PRESS PARA MOBILE
  // =============================================
  // Nota: ReactFlow não suporta onNodeTouchStart/End nativamente
  // Para implementar long press, deve-se adicionar listeners nos próprios componentes de nó customizados

  // =============================================
  // ADICIONAR NOVOS NÓS
  // =============================================

  const addNode = useCallback(
    (type: string) => {
      if (readOnly) return;

      triggerHapticFeedback('medium');
      const newNode: Node = {
        id: `${type}-${Date.now()}`,
        type,
        position: {
          x: Math.random() * 300 + 100,
          y: Math.random() * 300 + 100
        },
        data: getDefaultNodeData(type)
      };

      setNodes((nds) => [...nds, newNode]);
      setIsMobileToolbarOpen(false);
    },
    [setNodes, readOnly, triggerHapticFeedback]
  );

  const getDefaultNodeData = (type: string): any => {
    switch (type) {
      case 'start':
        return {
          label: 'Início',
          description: ''
        };
      case 'message':
        return {
          label: 'Mensagem WhatsApp',
          messageType: 'text',
          message: '',
          description: ''
        };
      case 'input':
        return {
          label: 'Capturar Entrada',
          inputType: 'text',
          variableName: '',
          description: ''
        };
      case 'condition':
        return {
          label: 'Condição',
          condition: '',
          description: ''
        };
      case 'action':
        return {
          label: 'Ação',
          actionType: 'save_data',
          description: ''
        };
      case 'webhook':
        return {
          label: 'Webhook/API',
          method: 'GET',
          url: '',
          headers: {},
          body: '',
          description: ''
        };
      case 'delay':
        return {
          label: 'Delay/Timer',
          delayType: 'seconds',
          delayValue: 1,
          description: ''
        };
      case 'database':
        return {
          label: 'Banco de Dados',
          operation: 'select',
          table: '',
          description: ''
        };
      case 'end':
        return {
          label: 'Fim',
          description: ''
        };
      case 'http_request':
        return {
          label: 'HTTP Request',
          method: 'GET',
          url: '',
          headers: {},
          body: '',
          responseVariable: '',
          description: ''
        };
      case 'javascript_function':
        return {
          label: 'Função JavaScript',
          code: '// Seu código aqui\nreturn { resultado: "ok" };',
          inputVariables: [],
          outputVariable: 'result',
          description: ''
        };
      case 'webhook_trigger':
        return {
          label: 'Webhook Trigger',
          webhookPath: '/webhook/flow-' + Date.now(),
          method: 'POST',
          authentication: 'none',
          description: ''
        };
      case 'internal_api':
        return {
          label: 'API Interna',
          endpoint: '',
          method: 'GET',
          entity: '',
          operation: 'read',
          description: ''
        };
      case 'whatsapp_advanced':
        return {
          label: 'WhatsApp',
          operation: 'send_text',
          category: 'messages',
          phoneNumber: '',
          messageType: 'text',
          description: ''
        };
      case 'loop':
        return {
          label: 'Loop',
          loopType: 'forEach',
          arrayVariable: 'items',
          itemVariable: 'item',
          condition: '',
          repeatCount: 10,
          description: ''
        };
      case 'error_handler':
        return {
          label: 'Try/Catch',
          retryCount: 3,
          retryDelay: 1000,
          continueOnError: false,
          description: ''
        };
      case 'email':
        return {
          label: 'Send Email',
          to: '',
          subject: '',
          body: '',
          template: '',
          cc: '',
          bcc: '',
          hasAttachments: false,
          description: ''
        };
      case 'scheduler':
        return {
          label: 'Agendamento',
          scheduleType: 'daily',
          cronExpression: '0 9 * * *',
          frequency: '',
          time: '09:00',
          timezone: 'America/Sao_Paulo',
          enabled: true,
          description: ''
        };
      case 'switch':
        return {
          label: 'Switch',
          variable: '',
          mode: 'value',
          cases: [
            { id: 'case-1', label: 'Caso 1', value: '' },
            { id: 'case-2', label: 'Caso 2', value: '' }
          ],
          defaultCase: true,
          description: ''
        };
      case 'set_variables':
        return {
          label: 'Definir Variáveis',
          assignments: [
            { id: 'var-1', name: '', value: '', type: 'string' }
          ],
          keepOnlySet: false,
          description: ''
        };
      default:
        return {
          label: 'Novo Nó',
          description: ''
        };
    }
  };

  // =============================================
  // DELETAR NÓ SELECIONADO
  // =============================================

  const deleteSelectedNode = useCallback(() => {
    if (!selectedNode || readOnly) return;

    triggerHapticFeedback('heavy');
    setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
    setEdges((eds) =>
      eds.filter(
        (edge) =>
          edge.source !== selectedNode.id && edge.target !== selectedNode.id
      )
    );
    setSelectedNode(null);
  }, [selectedNode, setNodes, setEdges, readOnly, triggerHapticFeedback]);

  // =============================================
  // SALVAR FLUXO
  // =============================================

  const handleSave = useCallback(() => {
    if (onSave) {
      triggerHapticFeedback('medium');
      onSave(nodes, edges);
    }
  }, [nodes, edges, onSave, triggerHapticFeedback]);

  // =============================================
  // ATUALIZAR NÓ APÓS EDIÇÃO
  // =============================================

  const handleNodeUpdate = useCallback(
    (nodeId: string, newData: any) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: { ...node.data, ...newData }
            };
          }
          return node;
        })
      );
      setIsEditModalOpen(false);
    },
    [setNodes]
  );

  // =============================================
  // CLIPBOARD - COPY/PASTE/CUT
  // =============================================

  const handleCopy = useCallback(() => {
    if (readOnly) return;

    const nodesToCopy = selectedNodes.length > 0 ? selectedNodes : (selectedNode ? [selectedNode] : []);
    if (nodesToCopy.length === 0) return;

    const nodeIds = new Set(nodesToCopy.map(n => n.id));
    const edgesToCopy = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));

    setClipboard({ nodes: nodesToCopy, edges: edgesToCopy });
  }, [selectedNode, selectedNodes, edges, readOnly]);

  const handlePaste = useCallback(() => {
    if (!clipboard || readOnly) return;

    const idMap = new Map<string, string>();
    const newNodes: Node[] = clipboard.nodes.map(node => {
      const newId = `${node.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      idMap.set(node.id, newId);

      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + 50, // Offset para não colar em cima
          y: node.position.y + 50
        }
      };
    });

    const newEdges: Edge[] = clipboard.edges.map(edge => ({
      ...edge,
      id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      source: idMap.get(edge.source) || edge.source,
      target: idMap.get(edge.target) || edge.target
    }));

    setNodes((nds) => [...nds, ...newNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
    setSelectedNodes(newNodes);
  }, [clipboard, setNodes, setEdges, readOnly]);

  const handleCut = useCallback(() => {
    if (readOnly) return;

    handleCopy();

    const nodesToCut = selectedNodes.length > 0 ? selectedNodes : (selectedNode ? [selectedNode] : []);
    const nodeIds = new Set(nodesToCut.map(n => n.id));

    setNodes((nds) => nds.filter(node => !nodeIds.has(node.id)));
    setEdges((eds) => eds.filter(edge => !nodeIds.has(edge.source) && !nodeIds.has(edge.target)));
    setSelectedNode(null);
    setSelectedNodes([]);
  }, [selectedNode, selectedNodes, handleCopy, setNodes, setEdges, readOnly]);

  const handleDuplicate = useCallback(() => {
    if (readOnly) return;

    handleCopy();
    handlePaste();
  }, [handleCopy, handlePaste, readOnly]);

  const handleSelectAll = useCallback(() => {
    if (readOnly) return;
    setSelectedNodes(nodes);
  }, [nodes, readOnly]);

  // =============================================
  // IMPORT/EXPORT
  // =============================================

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleExport = useCallback(() => {
    return { nodes, edges };
  }, [nodes, edges]);

  const handleImport = useCallback((data: { nodes: Node[], edges: Edge[] }) => {
    setNodes(data.nodes);
    setEdges(data.edges);
    setIsImportExportOpen(false);
  }, [setNodes, setEdges]);

  const handleSelectTemplate = useCallback((templateNodes: Node[], templateEdges: Edge[], mode: 'replace' | 'add') => {
    if (mode === 'replace') {
      setNodes(templateNodes);
      setEdges(templateEdges);
    } else {
      // Modo add - offset de 150px para evitar sobreposição
      const offsetNodes = templateNodes.map(node => ({
        ...node,
        id: `${node.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        position: {
          x: node.position.x + 150,
          y: node.position.y + 150
        }
      }));

      // Remapear edges para usar os novos IDs
      const idMap = new Map();
      templateNodes.forEach((oldNode, idx) => {
        idMap.set(oldNode.id, offsetNodes[idx].id);
      });

      const offsetEdges = templateEdges.map(edge => ({
        ...edge,
        id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        source: idMap.get(edge.source) || edge.source,
        target: idMap.get(edge.target) || edge.target
      }));

      setNodes(nds => [...nds, ...offsetNodes]);
      setEdges(eds => [...eds, ...offsetEdges]);
    }
  }, [setNodes, setEdges]);

  // =============================================
  // HISTÓRICO DE UNDO/REDO
  // =============================================

  const [history, setHistory] = useState<{ nodes: Node[], edges: Edge[] }[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const saveToHistory = useCallback(() => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ nodes, edges });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex, nodes, edges]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const previousState = history[historyIndex - 1];
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      setHistoryIndex(historyIndex - 1);
    }
  }, [historyIndex, history, setNodes, setEdges]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      setHistoryIndex(historyIndex + 1);
    }
  }, [historyIndex, history, setNodes, setEdges]);

  // =============================================
  // KEYBOARD SHORTCUTS
  // =============================================

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (readOnly) return;

      // Delete key
      if (event.key === 'Delete' && selectedNode) {
        deleteSelectedNode();
      }

      // Save shortcut (Ctrl/Cmd + S)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        handleSave();
      }

      // Undo shortcut (Ctrl/Cmd + Z)
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      }

      // Redo shortcut (Ctrl/Cmd + Shift + Z)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z') {
        event.preventDefault();
        redo();
      }

      // Copy shortcut (Ctrl/Cmd + C)
      if ((event.ctrlKey || event.metaKey) && event.key === 'c' && !event.shiftKey) {
        event.preventDefault();
        handleCopy();
      }

      // Paste shortcut (Ctrl/Cmd + V)
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.preventDefault();
        handlePaste();
      }

      // Cut shortcut (Ctrl/Cmd + X)
      if ((event.ctrlKey || event.metaKey) && event.key === 'x') {
        event.preventDefault();
        handleCut();
      }

      // Duplicate shortcut (Ctrl/Cmd + D)
      if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
        event.preventDefault();
        handleDuplicate();
      }

      // Select All shortcut (Ctrl/Cmd + A)
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        event.preventDefault();
        handleSelectAll();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedNode, deleteSelectedNode, handleSave, undo, redo, readOnly, handleCopy, handlePaste, handleCut, handleDuplicate, handleSelectAll]);

  // =============================================
  // RENDERIZAÇÃO DO MODO LISTA
  // =============================================

  const renderListView = () => (
    <div className="flow-list-view">
      <div className="flow-list-header">
        <h3>Nós do Fluxo ({nodes.length})</h3>
        <button
          onClick={() => setViewMode('diagram')}
          className="view-toggle-btn"
          title="Visualizar Fluxograma"
        >
          <i className="fas fa-project-diagram"></i> Ver Fluxograma
        </button>
      </div>
      <div className="flow-list-items">
        {nodes.map((node, index) => (
          <div
            key={node.id}
            className="flow-list-item"
            onClick={() => {
              setSelectedNode(node);
              setViewMode('diagram');
            }}
          >
            <div className="list-item-number">{index + 1}</div>
            <div className="list-item-content">
              <div className="list-item-type">
                {node.type === 'start' && <i className="fas fa-play-circle"></i>}
                {node.type === 'message' && <i className="fab fa-whatsapp"></i>}
                {node.type === 'input' && <i className="fas fa-keyboard"></i>}
                {node.type === 'condition' && <i className="fas fa-code-branch"></i>}
                {node.type === 'action' && <i className="fas fa-cog"></i>}
                {node.type === 'end' && <i className="fas fa-flag-checkered"></i>}
                <span>{node.data.label || 'Sem título'}</span>
              </div>
              {node.data.description && (
                <div className="list-item-description">{node.data.description}</div>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedNode(node);
                setIsEditModalOpen(true);
              }}
              className="list-item-edit"
            >
              <i className="fas fa-edit"></i>
            </button>
          </div>
        ))}
        {nodes.length === 0 && (
          <div className="flow-list-empty">
            <i className="fas fa-inbox"></i>
            <p>Nenhum nó adicionado ainda</p>
            <button onClick={() => setViewMode('diagram')} className="btn-primary">
              Adicionar Nós
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <div className="flow-editor-wrapper">
      {/* Node Palette - Desktop apenas */}
      {!readOnly && !isMobile && (
        <div className={`node-palette-container ${isNodePaletteOpen ? 'open' : 'closed'}`}>
          <NodePalette onAddNode={addNode} readOnly={readOnly} />
          <button
            className="node-palette-toggle"
            onClick={() => setIsNodePaletteOpen(!isNodePaletteOpen)}
            title={isNodePaletteOpen ? 'Fechar painel' : 'Abrir painel'}
          >
            <i className={`fas fa-chevron-${isNodePaletteOpen ? 'left' : 'right'}`}></i>
          </button>
        </div>
      )}

      <div className="flow-editor-container" ref={reactFlowWrapper}>
      {/* Toggle de Visualização Mobile */}
      {isMobile && !readOnly && (
        <div className="view-mode-toggle">
          <button
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
          >
            <i className="fas fa-list"></i> Lista
          </button>
          <button
            className={viewMode === 'diagram' ? 'active' : ''}
            onClick={() => setViewMode('diagram')}
          >
            <i className="fas fa-project-diagram"></i> Diagrama
          </button>
        </div>
      )}

      {viewMode === 'list' && isMobile ? renderListView() : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onNodeDoubleClick={onNodeDoubleClick}
            onSelectionChange={onSelectionChange}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            fitView
            // Mobile touch optimizations
            panOnScroll={!isMobile}
            panOnDrag={true}
            zoomOnScroll={!isMobile}
            zoomOnPinch={true}
            zoomOnDoubleClick={!isMobile}
            preventScrolling={true}
            minZoom={0.2}
            maxZoom={2}
            // Performance
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable={!readOnly}
            // Multi-selection
            selectionOnDrag={true}
            multiSelectionKeyCode="Shift"
            // Grid snapping
            snapToGrid={true}
            snapGrid={[20, 20]}
          >
            <Background color="#aaa" gap={20} />

            {/* Controles otimizados para mobile */}
            {!isMobile && <Controls />}

            {/* Minimapa flutuante - Desktop e Mobile */}
            <MiniMap
              nodeColor={(node) => {
                switch (node.type) {
                  case 'start': return '#48bb78';
                  case 'message': return '#25D366';
                  case 'input': return '#4299e1';
                  case 'condition': return '#ed8936';
                  case 'action': return '#9f7aea';
                  case 'end': return '#fc8181';
                  default: return '#ccc';
                }
              }}
              style={{
                width: isMobile ? 80 : 120,
                height: isMobile ? 60 : 80
              }}
              pannable={true}
              zoomable={true}
            />

          {/* Panel de ações - Desktop */}
          {!readOnly && !isMobile && (
            <Panel position="top-right" className="flow-panel">
              <div className="panel-actions">
                {selectedNode && (
                  <button
                    onClick={() => setIsEditModalOpen(true)}
                    className="panel-btn edit-btn"
                    title="Editar nó"
                  >
                    <i className="fas fa-edit"></i>
                    <span>Editar</span>
                  </button>
                )}
                {selectedNode && (
                  <button
                    onClick={deleteSelectedNode}
                    className="panel-btn delete-btn"
                    title="Deletar nó"
                  >
                    <i className="fas fa-trash"></i>
                    <span>Deletar</span>
                  </button>
                )}
                <button
                  onClick={handleFitView}
                  className="panel-btn fit-view-btn"
                  title="Centralizar fluxo"
                >
                  <i className="fas fa-compress-arrows-alt"></i>
                  <span>Centralizar</span>
                </button>
                <button
                  onClick={undo}
                  className="panel-btn undo-btn"
                  title="Desfazer (Ctrl+Z)"
                  disabled={historyIndex <= 0}
                >
                  <i className="fas fa-undo"></i>
                  <span>Desfazer</span>
                </button>
                <button
                  onClick={redo}
                  className="panel-btn redo-btn"
                  title="Refazer (Ctrl+Shift+Z)"
                  disabled={historyIndex >= history.length - 1}
                >
                  <i className="fas fa-redo"></i>
                  <span>Refazer</span>
                </button>
                <button
                  onClick={handleSave}
                  className="panel-btn save-btn"
                  title="Salvar fluxo (Ctrl+S)"
                >
                  <i className="fas fa-save"></i>
                  <span>Salvar</span>
                </button>
                <button
                  onClick={() => setIsImportExportOpen(true)}
                  className="panel-btn import-export-btn"
                  title="Backup/Restore fluxo"
                >
                  <i className="fas fa-file-export"></i>
                  <span>Backup</span>
                </button>
                <button
                  onClick={() => setIsTemplatesOpen(true)}
                  className="panel-btn templates-btn"
                  title="Usar template pronto"
                >
                  <i className="fas fa-sparkles"></i>
                  <span>Templates</span>
                </button>
                <button
                  onClick={() => setIsStatsOpen(true)}
                  className="panel-btn stats-btn"
                  title="Estatísticas e validação do fluxo"
                >
                  <i className="fas fa-chart-bar"></i>
                  <span>Estatísticas</span>
                </button>
              </div>
            </Panel>
          )}

          {/* Panel de Operações em Lote - Desktop */}
          {!readOnly && !isMobile && selectedNodes.length > 1 && (
            <Panel position="top-left" className="flow-panel bulk-operations-panel">
              <div className="bulk-operations-header">
                <i className="fas fa-layer-group"></i>
                <span>{selectedNodes.length} nós selecionados</span>
              </div>
              <div className="bulk-operations-grid">
                <div className="bulk-operations-section">
                  <label>Alinhar:</label>
                  <div className="bulk-btn-group">
                    <button onClick={() => alignNodes('left')} title="Alinhar à esquerda" className="bulk-btn">
                      <i className="fas fa-align-left"></i>
                    </button>
                    <button onClick={() => alignNodes('center-v')} title="Centralizar verticalmente" className="bulk-btn">
                      <i className="fas fa-align-center"></i>
                    </button>
                    <button onClick={() => alignNodes('right')} title="Alinhar à direita" className="bulk-btn">
                      <i className="fas fa-align-right"></i>
                    </button>
                  </div>
                  <div className="bulk-btn-group">
                    <button onClick={() => alignNodes('top')} title="Alinhar ao topo" className="bulk-btn">
                      <i className="fas fa-arrow-up"></i>
                    </button>
                    <button onClick={() => alignNodes('center-h')} title="Centralizar horizontalmente" className="bulk-btn">
                      <i className="fas fa-arrows-alt-v"></i>
                    </button>
                    <button onClick={() => alignNodes('bottom')} title="Alinhar à base" className="bulk-btn">
                      <i className="fas fa-arrow-down"></i>
                    </button>
                  </div>
                </div>
                {selectedNodes.length >= 3 && (
                  <div className="bulk-operations-section">
                    <label>Distribuir:</label>
                    <div className="bulk-btn-group">
                      <button onClick={() => distributeNodes('horizontal')} title="Distribuir horizontalmente" className="bulk-btn bulk-btn-wide">
                        <i className="fas fa-arrows-alt-h"></i>
                        <span>Horizontal</span>
                      </button>
                      <button onClick={() => distributeNodes('vertical')} title="Distribuir verticalmente" className="bulk-btn bulk-btn-wide">
                        <i className="fas fa-arrows-alt-v"></i>
                        <span>Vertical</span>
                      </button>
                    </div>
                  </div>
                )}
                <div className="bulk-operations-section">
                  <label>Visualização:</label>
                  <div className="bulk-btn-group">
                    <button onClick={zoomToSelection} title="Dar zoom na seleção" className="bulk-btn bulk-btn-wide bulk-btn-primary">
                      <i className="fas fa-search-plus"></i>
                      <span>Zoom na Seleção</span>
                    </button>
                  </div>
                </div>
              </div>
            </Panel>
          )}

          {/* FABs (Floating Action Buttons) - Mobile */}
          {!readOnly && isMobile && (
            <div className="flow-fabs">
              <button
                onClick={handleFitView}
                className="fab fab-fit-view"
                title="Centralizar fluxo"
              >
                <i className="fas fa-compress-arrows-alt"></i>
              </button>
              <button
                onClick={undo}
                className="fab fab-undo"
                title="Desfazer"
                disabled={historyIndex <= 0}
              >
                <i className="fas fa-undo"></i>
              </button>
              <button
                onClick={redo}
                className="fab fab-redo"
                title="Refazer"
                disabled={historyIndex >= history.length - 1}
              >
                <i className="fas fa-redo"></i>
              </button>
              <button
                onClick={handleSave}
                className="fab fab-save"
                title="Salvar"
              >
                <i className="fas fa-save"></i>
              </button>
              <button
                onClick={() => setIsMobileToolbarOpen(!isMobileToolbarOpen)}
                className={`fab fab-add ${isMobileToolbarOpen ? 'active' : ''}`}
                title="Adicionar nó"
              >
                <i className={`fas fa-${isMobileToolbarOpen ? 'times' : 'plus'}`}></i>
              </button>
            </div>
          )}
        </ReactFlow>
      )}

      {/* Toolbar Mobile */}
      {!readOnly && isMobile && (
        <MobileToolbar
          isOpen={isMobileToolbarOpen}
          onToggle={() => setIsMobileToolbarOpen(!isMobileToolbarOpen)}
          onAddNode={addNode}
          selectedNode={selectedNode}
          onEditNode={() => selectedNode && setIsEditModalOpen(true)}
          onDeleteNode={deleteSelectedNode}
          onSave={handleSave}
        />
      )}

      {/* Modal de Edição */}
      {selectedNode && isEditModalOpen && (
        <NodeEditorModal
          node={selectedNode}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleNodeUpdate}
          isMobile={isMobile}
        />
      )}

      {/* Modal de Import/Export */}
      <ImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        mode="export"
        flowData={{ nodes, edges }}
        onImport={handleImport}
      />

      {/* Modal de Templates */}
      <TemplatesModal
        isOpen={isTemplatesOpen}
        onClose={() => setIsTemplatesOpen(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      {/* Painel de Estatísticas */}
      <FlowStatsPanel
        isOpen={isStatsOpen}
        onClose={() => setIsStatsOpen(false)}
        nodes={nodes}
        edges={edges}
        onNodeClick={(nodeId) => {
          const node = nodes.find(n => n.id === nodeId);
          if (node) {
            setSelectedNode(node);
            // Zoom para o nó
            reactFlowInstance.setCenter(node.position.x + 100, node.position.y + 50, { zoom: 1.5, duration: 400 });
          }
        }}
      />
      </div>
    </div>
  );
};

// =============================================
// COMPONENTE WRAPPER PRINCIPAL
// =============================================

const FlowEditor: React.FC<FlowEditorProps> = (props) => {
  return (
    <ReactFlowProvider>
      <FlowEditorInner {...props} />
    </ReactFlowProvider>
  );
};

export default FlowEditor;
